import { format, parseISO } from "date-fns";
import moment from "moment";

const isDate = (dateStr: string | number) =>
  !Number.isNaN(new Date(dateStr).getDate());

export const dateFormatter = (date?: string): string => {
  if (!date || !isDate(date)) return "";
  return format(new Date(date.replaceAll("-", "/")), "MM/dd/yyyy");
};

export const dateFormatterInputField = (date: string): string => {
  if (!isDate(date)) return "";
  return format(parseISO(new Date(date).toISOString()), "yyyy-MM-dd");
};

export const dateWithTimestampFormatter = (date: string): string =>
  format(new Date(date), "MM/dd/yyyy");

export const formatPhoneNumber = (phoneNumberString: string): string => {
  const cleaned = `${phoneNumberString}`.replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return "";
};

export const formatPhoneNumberWithInternational = (
  str: string | null,
): string => {
  if (!str) return "";

  const cleaned = `${str}`.replace(/\D/g, "");

  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    const intlCode = match[1] ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }
  return "";
};

export const capitalizeFirstLetter = (phrase: string): string =>
  phrase
    .split(" ")
    .map(
      (word) =>
        word.toLowerCase().charAt(0).toUpperCase() +
        word.toLowerCase().slice(1),
    )
    .join(" ");

export const fetchOrLogout = async (
  input: RequestInfo,
  init: RequestInit | undefined,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  let response: Response;
  try {
    response = await fetch(input, init);
    if (response.status === 401) {
      setLoggedOut(true);
    }
  } catch (e) {
    //
  }
  return new Promise((resolve) => resolve(response)) as Promise<Response>;
};

export const fetchWithCatch = async (
  input: RequestInfo,
  init: RequestInit | undefined,
  setLoggedOut: (inactivity: boolean) => void,
  errorSetter?: (error: string) => void,
): Promise<Response> => {
  let response: Response;
  try {
    response = await fetchOrLogout(input, init, setLoggedOut);
  } catch (e) {
    if (errorSetter) {
      errorSetter("There was a network issue - please try again.");
    }
  }
  return new Promise((resolve) => resolve(response)) as Promise<Response>;
};

export const networkErrorMessage =
  "Error connecting - please check your network connection.";

export const numberWithCommas = (x: number): string =>
  x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const getWeeksDiffGestation = (date1: Date, date2: Date): number =>
  Math.floor(40 - (date1.getTime() - date2.getTime()) / (1000 * 3600 * 24 * 7));

export const clearHash = (): void => {
  const uri = window.location.toString();
  if (uri.indexOf("#") > 0) {
    const cleanUri = uri.substring(0, uri.indexOf("#"));
    window.history.replaceState({}, document.title, cleanUri);
  }
};

export const getNumberOfDaysDifference = (date1: Date, date2: Date): number =>
  Math.floor((date2.getTime() - date1.getTime()) / (1000 * 3600 * 24));

export const getTrimester = (estimatedDueDate: Date): number => {
  const numberWeeksPregnant = getWeeksDiffGestation(
    estimatedDueDate,
    new Date(),
  );
  const firstTrimesterCutoff = 14; // 0-13 weeks (up to but not including start of week 14)
  const secondTrimesterCutoff = 27; // 14-26 weeks (up to but not including start of week 27)
  if (numberWeeksPregnant < firstTrimesterCutoff) {
    return 1;
  }
  if (
    numberWeeksPregnant >= firstTrimesterCutoff &&
    numberWeeksPregnant < secondTrimesterCutoff
  ) {
    return 2;
  }
  return 3; // otherwise in third trimester
};

// Strictly matches a `YYYY-MM-DD` calendar-date string. Anything else --
// timestamps, slash-separated dates, locale strings, etc. -- must go through
// a different parser, since the noon-UTC anchor below is only meaningful for
// a bare calendar day.
const YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// `new Date("YYYY-MM-DD")` parses as UTC midnight, which renders as the
// previous calendar day in any negative-UTC timezone (i.e. every US clinic).
// Anchor at noon UTC so the resulting Date represents the same calendar day
// in every timezone from UTC-12 through UTC+11 (covers every US clinic and
// the bulk of the inhabited globe -- see the timezone tests for the UTC+12
// boundary, where noon UTC rolls over to the next local day). Returns an
// Invalid Date for empty / unparseable input rather than concatenating
// garbage into the ISO string, so upstream `!` assertions failing here
// surface as NaN math instead of silently shifting by an unknown amount.
export const parseYmdAtNoonUtc = (date?: string | null): Date => {
  if (!date || !YMD_REGEX.test(date)) return new Date(NaN);
  const parsed = new Date(`${date}T12:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return parsed;
  // V8 catches obvious garbage like `2023-13-01` or `2023-01-67` (returns
  // Invalid Date), but it silently rolls calendar overflow forward:
  // `2023-02-30` parses as Mar 2, and non-leap-year `2023-02-29` parses as
  // Mar 1. Either would re-introduce the same off-by-one bug class this
  // helper exists to fix. Re-emit the YMD from the parsed UTC components
  // and reject if it doesn't round-trip -- this is calendar-aware (leap
  // years still parse correctly) without needing to encode month lengths
  // or leap-year rules in a regex.
  const roundTrip =
    `${parsed.getUTCFullYear().toString().padStart(4, "0")}` +
    `-${(parsed.getUTCMonth() + 1).toString().padStart(2, "0")}` +
    `-${parsed.getUTCDate().toString().padStart(2, "0")}`;
  return roundTrip === date ? parsed : new Date(NaN);
};

export const formatWithTimezoneOffset = (date?: string | null): string => {
  const parsed = parseYmdAtNoonUtc(date);
  if (Number.isNaN(parsed.getTime())) return "";
  return format(parsed, "MM/dd/yyyy");
};

export const formatDateTimeFromEpoch = (date?: number | string): string => {
  if (!date || !isDate(date)) return "";
  return format(moment.utc(date).toDate(), "MM/dd/yyyy',' h:mm a");
};

export const defaultSort = (valueA: number, valueB: number): number => {
  if (valueA === valueB) return 0;
  if (Number.isNaN(valueA) || Number.isNaN(valueB)) return -1;
  return valueA > valueB ? 1 : -1;
};
